/**
 * Tooth Chart V1 — collapsed 5-status palette.
 *
 * Drives the top-of-crown status dot rendered by AnatomicalTooth and the
 * per-surface dots inside M/D/B/L/O zones. Procedure-history badges
 * (R/C/F/M/B/X/I) keep their existing kind-specific tints in
 * AnatomicalTooth's MARKER_TINT — those convey "what was done" and are
 * NOT replaced by this status system, which conveys "what's happening
 * now."
 */

export const STATUS_COLORS = {
  urgent:      '#dc2626',  // red-600     — severe diagnosis
  diagnosis:   '#ea580c',  // orange-600  — moderate diagnosis / default
  watch:       '#ca8a04',  // yellow-600  — mild diagnosis / monitor
  planned:     '#2563eb',  // blue-600    — sitting planned, not yet started
  in_progress: '#0d9488',  // teal-600    — sitting actively underway
  completed:   '#16a34a',  // green-600   — sitting completed
};

const URGENT_RING = '#fca5a5';  // red-300, only used as urgent outer ring

const STATUS_LABEL = {
  urgent: 'Urgent',
  diagnosis: 'Diagnosis',
  watch: 'Watch',
  planned: 'Planned',
  in_progress: 'In progress',
  completed: 'Completed',
};


/**
 * Resolve the highest-priority status for a tooth from its marker shape
 * (the union of diagnosis + procedure-sitting info produced by
 * usePatientToothHistory). Returns null when nothing on the tooth
 * warrants a status dot.
 *
 * Priority order: urgent → diagnosis → watch → planned → completed.
 * A tooth with BOTH an active diagnosis AND a planned procedure renders
 * the diagnosis dot plus a thin blue secondary ring (set via
 * `secondaryRing: STATUS_COLORS.planned`).
 */
export function resolveStatus(marker) {
  if (!marker) return null;

  const hasDiagnosis = !!marker.has_diagnosis || marker.kind === 'diagnosis';
  const severity = marker.severity || null;
  const hasPlanned = marker.planned_count > 0 || marker.has_planned;
  const hasInProgress = marker.in_progress_count > 0
    || (typeof marker.planned_status === 'string'
        && marker.planned_status.toLowerCase() === 'in_progress');
  const hasCompleted = marker.completed_count > 0 || marker.has_completed;
  // F6.4 — a diagnosis whose linked procedures are ALL completed (no
  // pending plans) is clinically resolved. Promote to green so the
  // dentist can see at a glance that this finding has been addressed.
  const isResolvedDiagnosis = hasDiagnosis
    && marker.has_resolved_diagnosis
    && !hasPlanned;

  let primary = null;
  if (isResolvedDiagnosis) primary = 'completed';
  else if (hasDiagnosis && severity === 'severe') primary = 'urgent';
  else if (hasDiagnosis && severity === 'mild') primary = 'watch';
  else if (hasDiagnosis) primary = 'diagnosis';
  else if (hasInProgress) primary = 'in_progress';
  else if (hasPlanned) primary = 'planned';
  else if (hasCompleted) primary = 'completed';

  if (!primary) return null;

  // The chart's half-fill renderer uses fillStatus to pick the level
  // (¼ blue → ⅔ teal → full green). For diagnosis statuses (urgent,
  // diagnosis, watch) the secondaryRing reflects the active procedure
  // state if one exists — choosing teal when in_progress over blue.
  const secondaryFillStatus = hasInProgress
    ? 'in_progress'
    : (hasPlanned ? 'planned' : null);

  return {
    key: primary,
    label: STATUS_LABEL[primary],
    fill: STATUS_COLORS[primary],
    ring: primary === 'urgent' ? URGENT_RING : null,
    // Diagnosis + active procedure together → stack the procedure's
    // state ring (blue or teal) on the diagnosis dot.
    secondaryRing:
      (primary === 'urgent' || primary === 'diagnosis' || primary === 'watch')
      && secondaryFillStatus
        ? STATUS_COLORS[secondaryFillStatus]
        : null,
    // Effective status string for the half-fill renderer. For "planned"
    // primary this is 'planned' or 'in_progress'; for diagnosis primary
    // this is the secondary state (so a diagnosed tooth with a live
    // procedure still gets the teal mid-fill on its secondary ring).
    fillStatus: primary === 'in_progress' ? 'in_progress'
      : primary === 'planned' ? 'planned'
      : primary === 'completed' ? 'completed'
      : secondaryFillStatus,
  };
}
